import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import theme from './BuyerDocuments.module.scss';
import { IState } from '@/state/IState';
import { BuyerDocument, BuyerOfferDocumentGroup } from '@/types';
import { getBuyerDocuments, getBuyerOfferDocuments } from '@/services/buyer-dashboard';
import { FileIcon, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import BuyerOfferDocumentCardList from './BuyerDocumentCardList/BuyerOfferDocumentCardList';
import { DocumentTypeGrouping, DocumentTypeGroupingHeader } from '@/components/DocumentTypeGrouping';
import { NoResultsCard } from '@/components/Cards';
import BuyerPageTitle from '../BuyerPageTitle/BuyerPageTitle';
import { Button, ButtonBar, CreateTableComponent, TableContainer, TableHeader, useBreakpoint } from '@ygtechnologies/rubus-ui';
import { faBuilding, faDownload, faHistory, faHome } from '@fortawesome/pro-light-svg-icons';
import { useDocumentType } from '@/utils/hooks';
import { DateDisplay } from '@/components/localization';
import BuyerDocumentsHistoryDialog from './BuyerDocumentsHistoryDialog';
import { getDownloadOriginalDocumentUrl, getOpenDocumentUrl } from '@/services/documents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const mapDispatchToProps = (dispatch: Dispatch) => ({});
const mapStateToProps = (state: IState) => ({
    permissions: state.buyer.permissions!,
});

type BuyerDocumentsProps = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> & {
        versionId?: string;
    };

const Table = CreateTableComponent<BuyerDocument>();

const BuyerDocuments: React.FC<BuyerDocumentsProps> = ({ permissions, versionId }) => {
    const activeVersionId = versionId ? +versionId : null;
    const [documents, setDocuments] = useState<BuyerDocument[] | null>(null);
    const [offerDocuments, setOfferDocuments] = useState<BuyerOfferDocumentGroup[] | null>(null);

    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [activeDocument, setActiveDocument] = useState<BuyerDocument | null>(null);

    const isDesktop = useBreakpoint().up('lg');

    const handleHistoryClick = (document: BuyerDocument) => {
        setHistoryDialogOpen(true);
        setActiveDocument(document);
    };

    useEffect(() => {
        async function loadData() {
            if (permissions.residentialUnitId) {
                var [normal, offer] = await Promise.all([
                    getBuyerDocuments(permissions.residentialUnitId, []),
                    getBuyerOfferDocuments(permissions.residentialUnitId),
                ]);

                setDocuments(normal);
                setOfferDocuments(offer);
            }
        }

        loadData();
    }, [permissions]);

    const renderContent = () => {
        if (!documents || !offerDocuments) {
            return <LoadingIndicator />;
        }

        if (documents.length === 0 && offerDocuments.length === 0) {
            return (
                <NoResultsCard>
                    <Trans>No documents</Trans>
                </NoResultsCard>
            );
        }

        return (
            <>
                {/* <DocumentTypeGrouping items={documents} emptyText={null}>
                    {(items, groupInfo, key) => (
                        <React.Fragment key={key}>
                            <DocumentTypeGroupingHeader value={groupInfo} />
                            <BuyerDocumentsCards documents={items} />
                        </React.Fragment>
                    )}
                </DocumentTypeGrouping> */}

                {offerDocuments.length > 0 && (
                    <>
                        <div className={theme.groupHeader}>
                            <Trans>Change requests</Trans>
                        </div>
                        <BuyerOfferDocumentCardList groups={offerDocuments} />
                    </>
                )}
            </>
        );
    };
    if (!documents || !offerDocuments) {
        return <LoadingIndicator />;
    }
    const fixedDateDocuments = documents.map((document) => {
        return {
            ...document,
            uploadDate: new Date(document.uploadDate),
        };
    });
    return (
        <div className={theme.container}>
            <BuyerPageTitle>
                <Trans>Documents</Trans>
            </BuyerPageTitle>
            <DocumentTypeGrouping items={fixedDateDocuments} emptyText={null}>
                {(items, groupInfo, key) => (
                    <TableContainer key={key} className={theme.tableContainer}>
                        <TableHeader className={theme.tableHeader}>
                            <DocumentTypeGroupingHeader value={groupInfo} />
                        </TableHeader>
                        <Table
                            preSortedBy={versionId ? { field: 'uploadDate', direction: 'desc' } : undefined}
                            className={theme.table}
                            loading={documents === null}
                            data={items}
                            sortingMode="client"
                            getRowId={(row) => row.documentId}>
                            <Table.Column
                                sortable
                                header={t`File name`}
                                renderer={({ row }) => <FileNameColumnRender document={row} />}
                                field="documentTypeId"
                            />

                            <Table.Column
                                sortable
                                size={140}
                                hidden={!isDesktop}
                                field="uploadDate"
                                header={t`Modified`}
                                renderer={({ row }) => <DateDisplay value={row.uploadDate} />}
                            />
                            <Table.Column hidden={!isDesktop} field="versionNumber" header={t`Version`} size={100} />

                            <Table.Column
                                size={isDesktop ? 300 : 0}
                                renderer={({ row }) => (
                                    <ButtonBar margins="none" noWrap>
                                        <Button
                                            as="a"
                                            size={isDesktop ? 'md' : 'sm'}
                                            variant={row.versionId == activeVersionId ? 'primary' : 'tertiary'}
                                            href={getDownloadOriginalDocumentUrl(row.versionId)}
                                            target="_blank"
                                            icon={faDownload}>
                                            {isDesktop ? <Trans>Download</Trans> : undefined}
                                        </Button>
                                        <Button
                                            size={isDesktop ? 'md' : 'sm'}
                                            variant="tertiary"
                                            onClick={() => handleHistoryClick(row)}
                                            icon={faHistory}>
                                            {isDesktop ? <Trans>Show history</Trans> : undefined}
                                        </Button>
                                    </ButtonBar>
                                )}
                            />
                        </Table>
                    </TableContainer>
                )}
            </DocumentTypeGrouping>

            {renderContent()}
            {activeDocument && (
                <BuyerDocumentsHistoryDialog
                    document={activeDocument}
                    show={historyDialogOpen}
                    toggle={setHistoryDialogOpen}
                />
            )}
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(BuyerDocuments);

interface FileNameColumnProps {
    document: BuyerDocument;
}

const FileNameColumnRender = ({ document }: FileNameColumnProps) => {
    const documentType = useDocumentType(document.documentTypeId);

    return (
        <div className={theme.nameRow}>
            <FileIcon fixedWidth className={theme.fileIcon} value={document.fileMetadata} />
            <a
                className={'link-unstyled'}
                target="_blank"
                rel="noopener noreferrer"
                href={getOpenDocumentUrl(document.versionId)}>
                <span>
                    <FontAwesomeIcon icon={document.isProjectDocument ? faBuilding : faHome} />
                    <span className={theme.documentName}>{document.name}</span>
                </span>
                <span className={theme.muted}>{documentType?.name}</span>
            </a>
        </div>
    );
};
